import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * StartBuildingButton — the mint pill that lives in every page header
 * EXCEPT the Generator and the Skills Library (where teachers are
 * either already building or browsing content directly).
 *
 * Routing decision happens at click-time via `to`:
 *   • signed-in user with an active plan / admin → /generator
 *   • everyone else → /pricing (so they can sign up or upgrade)
 *
 * The /generator page itself has paywall logic that bounces free
 * users back to /pricing — but routing to /pricing first for
 * signed-out users avoids a flash to /generator and back.
 */
const StartBuildingButton = ({
  className = "",
  testid = "header-start-building",
  label = "Start Building",
  // Override destination — used by `<BuildASkillButton />` to send teachers
  // to the Skills Library instead of the generator. When omitted, the
  // button auto-routes based on auth + plan state.
  toOverride,
}) => {
  const { user } = useAuth();
  const target =
    toOverride ||
    (user && (user?.subscription?.is_active || user?.is_admin)
      ? "/generator"
      : "/pricing");
  return (
    <Link
      to={target}
      data-testid={testid}
      className={
        "inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-extrabold tracking-tight bg-[#96ded1] text-[#1c2d3d] hover:bg-white shadow-lg shadow-[#96ded1]/20 hover:shadow-[#96ded1]/40 transition-all hover:-translate-y-0.5 " +
        className
      }
    >
      {label}
      <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  );
};

/**
 * BuildASkillButton — sibling pill that always routes to the Skills
 * Library. Used in homepage sections that highlight transferable
 * skills (Career Connection, Scan/Process/Execute framework). Same
 * visual treatment as StartBuildingButton so the brand feels
 * cohesive across the marketing page.
 */
export const BuildASkillButton = ({ testid = "build-a-skill", className = "" }) => (
  <StartBuildingButton
    label="Build a Skill"
    toOverride="/skills"
    testid={testid}
    className={className}
  />
);

export default StartBuildingButton;
